import React, { useState, useEffect, useCallback, useRef } from "react";
import confetti from "canvas-confetti";
import { X, Sparkles, ArrowRight } from "lucide-react";
import api from "@/lib/api";

const DONE_KEY = "bs_takeoff_tour_done";

// group = the "Step N of 3" label the user sees; several micro-steps can share a group.
const STEPS = [
  {
    group: 1, tab: "takeoff", target: "takeoff-tool-calibrate", desktopOnly: true,
    title: "Set the scale",
    body: "Tap Calibrate, then click each end of something you know the length of — a 1 m line or a standard door — and type the real length. Now every measurement is spot-on.",
    tip: "On a PDF? Tap “Detect measurements” and we’ll set the scale for you.",
    advance: { event: "tradeoff:calibrated" },
  },
  {
    group: 2, tab: "takeoff", target: "takeoff-tool-line", desktopOnly: true,
    title: "Measure your first run",
    body: "Choose Trace / Measure, click the start point (A), then the end point (B). The length appears instantly in metres.",
    advance: { event: "tradeoff:line-added" },
  },
  {
    group: 3, tab: "takeoff", target: "tab-estimate",
    title: "Now price it up",
    body: "Open the Estimate tab to turn your measurements into a priced list.",
    advance: { clickTarget: true, goTab: "estimate" },
  },
  {
    group: 3, tab: "estimate", target: "materials-tag-bar",
    title: "Add a material",
    body: "Tap a material like “Facing Bricks” to drop in a priced line — the right unit is filled in for you.",
    advance: { event: "tradeoff:estimate-line-added" },
  },
  {
    group: 3, tab: "estimate", target: "tab-billing",
    title: "Turn it into a bill",
    body: "When you’re ready, open Billing to raise an Application for Payment or invoice from your estimate.",
    advance: { clickTarget: true, finish: true },
  },
];
const TOTAL_GROUPS = 3;

const TakeoffTour = ({ tab, setTab, tourDone, isSample = false }) => {
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [celebrate, setCelebrate] = useState(false);
  const iRef = useRef(0);
  const isSampleRef = useRef(isSample);
  isSampleRef.current = isSample;

  const step = active ? STEPS[i] : null;

  const finish = useCallback(() => {
    try { localStorage.setItem(DONE_KEY, "1"); } catch (e) {}
    api.post("/me/tour-complete").catch(() => {});
    setActive(false);
    setCelebrate(true);
    try {
      confetti({ particleCount: 120, spread: 75, startVelocity: 45, origin: { y: 0.6 }, colors: ["#2563EB", "#16305C", "#22c55e", "#ffffff"] });
    } catch (e) {}
    setTimeout(() => setCelebrate(false), 6000);
  }, []);

  const next = useCallback(() => {
    setI((prev) => {
      const n = prev + 1;
      if (n >= STEPS.length) { finish(); return prev; }
      iRef.current = n;
      return n;
    });
  }, [finish]);

  // Manual advance (needed on mobile, where the takeoff tools are view-only and the
  // action-based auto-advance can't fire). Honours a step's goTab/finish intent.
  const manualNext = useCallback(() => {
    const s = STEPS[iRef.current];
    if (!s) { next(); return; }
    if (s.advance?.goTab) setTab(s.advance.goTab);
    if (s.advance?.finish) { finish(); return; }
    next();
  }, [next, finish, setTab]);

  const start = useCallback(() => { setCelebrate(false); setI(0); iRef.current = 0; setActive(true); }, []);
  const skip = useCallback(() => { try { localStorage.setItem(DONE_KEY, "1"); } catch (e) {} setActive(false); }, []);

  // Launch triggers: manual replay always; auto-start once inside the DEMO project only
  // (so the tour never surprises users on their own real jobs).
  useEffect(() => {
    const onReplay = () => start();
    const onReady = () => { if (isSampleRef.current && !localStorage.getItem(DONE_KEY) && !tourDone) start(); };
    window.addEventListener("tradeoff:start-tour", onReplay);
    window.addEventListener("tradeoff:takeoff-ready", onReady);
    return () => {
      window.removeEventListener("tradeoff:start-tour", onReplay);
      window.removeEventListener("tradeoff:takeoff-ready", onReady);
    };
  }, [start, tourDone]);

  // Keep the tour on the right tab for the current step.
  useEffect(() => {
    if (step && step.tab && step.tab !== tab) setTab(step.tab);
  }, [step, tab, setTab]);

  // Locate the highlighted element (poll briefly while tab content mounts) and track it.
  useEffect(() => {
    if (!step) return undefined;
    let tries = 0, raf;
    const locate = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) {
        setRect(el.getBoundingClientRect());
      } else if (tries++ < 25) {
        raf = setTimeout(locate, 120);
      } else {
        setRect(null);
      }
    };
    locate();
    const onMove = () => {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { clearTimeout(raf); window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [step, tab]);

  // Advance on the real user action for this step.
  useEffect(() => {
    if (!step) return undefined;
    if (step.advance.event) {
      const h = () => next();
      window.addEventListener(step.advance.event, h);
      return () => window.removeEventListener(step.advance.event, h);
    }
    if (step.advance.clickTarget) {
      const el = document.querySelector(`[data-testid="${step.target}"]`);
      if (!el) return undefined;
      const h = () => { if (step.advance.goTab) setTab(step.advance.goTab); if (step.advance.finish) finish(); else next(); };
      el.addEventListener("click", h, { once: true });
      return () => el.removeEventListener("click", h);
    }
    return undefined;
  }, [step, next, finish, setTab]);

  if (celebrate) {
    return (
      <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/30 p-4" data-testid="tour-complete">
        <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-blue-50 flex items-center justify-center mb-3"><Sparkles className="h-6 w-6 text-blue-600" /></div>
          <h3 className="font-display font-bold text-lg text-navy">You did it! 💪</h3>
          <p className="text-sm text-slate-600 mt-1.5">You just went from drawing to bill in under a minute. You’re ready to take off your own project.</p>
          <div className="mt-4 flex gap-2 justify-center">
            <button onClick={() => setCelebrate(false)} data-testid="tour-done-btn" className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 h-10">Start working</button>
            <button onClick={() => { setCelebrate(false); start(); }} data-testid="tour-replay-btn" className="rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold px-4 h-10">Replay tour</button>
          </div>
        </div>
      </div>
    );
  }

  if (!active || !step) return null;

  // Tooltip placement: below the target if room, else above; clamped to viewport.
  const vw = window.innerWidth, vh = window.innerHeight;
  const isMobile = vw < 1024;
  const cardW = Math.min(340, vw - 24);
  let top, left, below = true;
  if (rect) {
    below = rect.bottom + 200 < vh;
    top = below ? rect.bottom + 12 : Math.max(12, rect.top - 12);
    left = Math.min(Math.max(12, rect.left), vw - cardW - 12);
  } else {
    top = vh / 2 - 100; left = vw / 2 - cardW / 2;
  }

  return (
    <div className="fixed inset-0 z-[80]" style={{ pointerEvents: "none" }} data-testid="takeoff-tour">
      {/* Spotlight cut-out (visual only) */}
      {rect && (
        <div
          className="absolute rounded-lg"
          style={{
            top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
            boxShadow: "0 0 0 9999px rgba(15,23,42,0.55)",
            outline: "2px solid #2563EB", outlineOffset: "2px",
            transition: "all 0.25s ease",
          }}
        />
      )}
      {/* Coach card */}
      <div
        className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 p-4"
        style={{ top, left, width: cardW, transform: below ? "none" : "translateY(-100%)", pointerEvents: "auto" }}
      >
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">Step {step.group} of {TOTAL_GROUPS}</span>
          <button onClick={skip} data-testid="tour-skip" className="text-slate-400 hover:text-slate-600" aria-label="Skip tour"><X className="h-4 w-4" /></button>
        </div>
        <h4 className="font-display font-bold text-slate-900 text-base">{step.title}</h4>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{step.body}</p>
        {step.desktopOnly && isMobile && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 rounded-md px-2.5 py-1.5" data-testid="tour-mobile-note">
            📱 You're on a phone, so this is a quick preview — you'll actually do this step on a tablet or computer, where the takeoff tools appear.
          </p>
        )}
        {step.tip && <p className="mt-2 text-xs text-blue-700 bg-blue-50 rounded-md px-2.5 py-1.5">💡 {step.tip}</p>}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex gap-1.5">
            {[1, 2, 3].map((g) => <span key={g} className={`h-1.5 rounded-full transition-all ${g === step.group ? "w-5 bg-blue-600" : "w-1.5 bg-slate-300"}`} />)}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={skip} data-testid="tour-skip-link" className="text-xs text-slate-400 hover:text-slate-600">Skip</button>
            <button onClick={manualNext} data-testid="tour-next" className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold px-3.5 h-8 inline-flex items-center gap-1">
              {i >= STEPS.length - 1 ? "Finish" : "Next"} <ArrowRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TakeoffTour;
